import React, { useState, useEffect, useMemo, useRef } from "react";
import { Breadcrumb } from "../../components/Breadcrumb";
import { FORM_CONSTANTS } from "./constants";
import { ClipboardDocumentCheckIcon } from "@heroicons/react/24/outline";
import { useLocation, useParams } from "react-router-dom";
import { FormData, ProjectOptionType } from "./types";
import { ActionButtons } from "../../utils/renderActionButtons";
import { useBonusDetails, useJsonTemplates, useProjects } from "../../hooks/useBonusQueries";
import { BonusApplicationComment, Project } from "../../services/bonus/types";
import { getPreviousQuarter } from "../../utils/dateUtils";
import Select, { components, GroupHeadingProps, GroupProps, StylesConfig } from 'react-select';
import SubdivisionProductionSubForm from "../../components/SubForms/SubdivisionProductionSubForm";
import ExtraLaborAndMaterialSubForm from "../../components/SubForms/ExtraLaborAndMaterialSubForm";
import CustomerCareExpenseSubForm from "../../components/SubForms/CustomerCareExpenseSubForm";
import CustomerCareCallsSubForm from "../../components/SubForms/CustomerCareCallsSubForm";
import PostCloseTouchpointSubForm from "../../components/SubForms/PostCloseTouchpointSubForm";
import SurveysSubForm from "../../components/SubForms/SurveysSubForm";
import BonusCalculationsSPMSubForm from "../../components/SubForms/BonusCalculationsSPMSubForm";
import CommentComponent from "../../components/SubForms/Common/CommentComponent";
import BonusApplicationHistory from "../../components/SubForms/Common/BonusApplicationHistory";
import { useComments, useHistory } from "../../hooks/useFormHelpers";
import { getBonusSheetJsonData, parseBonusSheetJsonData } from "../../utils/bonusSheetParser";

// ── react-select styles that respect CSS variables ────────────────────────────
const getSelectStyles = (): StylesConfig<ProjectOptionType, true> => ({
  control: (base, state) => ({
    ...base,
    backgroundColor: "var(--bg-input)",
    borderColor: state.isFocused ? "var(--border-focus)" : "var(--border-default)",
    boxShadow: state.isFocused ? "0 0 0 3px var(--focus-ring)" : "none",
    color: "var(--text-primary)",
    fontSize: 13,
    minHeight: 36,
    "&:hover": { borderColor: "var(--border-hover)" },
  }),
  menu: base => ({ ...base, background: "var(--bg-surface)", border: "1px solid var(--border-default)", boxShadow: "0 8px 24px rgba(0,0,0,0.12)", zIndex: 50 }),
  option: (base, state) => ({ ...base, background: state.isFocused ? "var(--bg-chip-hover)" : "transparent", color: "var(--text-primary)", fontSize: 13, cursor: "pointer" }),
  multiValue: base => ({ ...base, background: "var(--bg-chip)", borderRadius: 4 }),
  multiValueLabel: base => ({ ...base, color: "var(--text-primary)", fontSize: 12 }),
  multiValueRemove: base => ({ ...base, color: "var(--text-muted)", "&:hover": { background: "var(--border-default)", color: "var(--text-primary)" } }),
  placeholder: base => ({ ...base, color: "var(--text-muted)", fontSize: 13 }),
  singleValue: base => ({ ...base, color: "var(--text-primary)" }),
  input: base => ({ ...base, color: "var(--text-primary)" }),
  indicatorSeparator: base => ({ ...base, background: "var(--border-default)" }),
  dropdownIndicator: base => ({ ...base, color: "var(--text-muted)", "&:hover": { color: "var(--text-secondary)" } }),
  clearIndicator: base => ({ ...base, color: "var(--text-muted)", "&:hover": { color: "var(--text-secondary)" } }),
});

// Helper for immutable nested state updates
const setByPath = <T,>(obj: T, path: string[], value: any): T => {
  const [head, ...rest] = path;
  if (!head) return obj;
  return { ...(obj as any), [head]: rest.length ? setByPath((obj as any)[head] ?? {}, rest, value) : value } as T;
};

const CustomCommunityGroupHeading = ({
  toggleGroup,
  collapsedGroups,
  ...props
}: any) => {
  const isCollapsed = collapsedGroups.includes(props.children as string);
  return (
    <div
      onClick={() => toggleGroup(props.children as string)}
      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
    >
      <components.GroupHeading {...props} />
      <span style={{ fontSize: 10, marginRight: 10 }}>
        {isCollapsed ? '▶' : '▼'}
      </span>
    </div>
  );
};

const CustomCommunityGroup = ({ collapsedGroups, ...props }: any) => {
  const isCollapsed = collapsedGroups.includes(props.label as string);
  return (
    <components.Group {...props}>
      {!isCollapsed && props.children}
    </components.Group>
  );
};

const SPMForm: React.FC = () => {
  const [collapsedCommunityGroups, setCollapsedCommunityGroups] = useState<string[]>([]);
  const location = useLocation();
  const selectedRole = localStorage.getItem("selectedRole") || "";
  const { id } = useParams<{ id: string }>();
  const numericId = parseInt(id || "0");
  const hasValidId = numericId > 0;
  const { data: templates, isLoading: isLoadingTemplates } = useJsonTemplates(selectedRole);
  const { data: bonusDetails, isLoading: isLoadingBonusDetails } = useBonusDetails(numericId);
  const { data: projectsData, isLoading: isLoadingProjects } = useProjects();
  const [bonusSheetData, setBonusSheetData] = useState<any>(null);

  const under7DebounceRef = useRef<NodeJS.Timeout | null>(null);
  const meetingsDebounceRef = useRef<NodeJS.Timeout | null>(null);

  const queryParams = new URLSearchParams(location.search);
  const isReviewMode = queryParams.get("mode") === "review";
  const prevQuarterYear = getPreviousQuarter();
  const editableStatuses = [null, "Draft", "Rejected"];
  const isEditable = editableStatuses.includes(bonusDetails?.currentStatus ?? null);

  const isLoading = isLoadingTemplates || isLoadingBonusDetails || isLoadingProjects;
  const projects = Array.isArray(projectsData) ? projectsData : [];
  const initQueryParam = queryParams.get("init") || null;

  const [bonusSheetTitle, setBonusSheetTitle] = useState<string>(`${selectedRole} Quarterly Bonus`);
  const [selectedProjectOptions, setSelectedProjectOptions] = useState<ProjectOptionType[]>([]);
  const [projectOptions, setProjectOptions] = useState<ProjectOptionType[]>([]);
  const [applicantInfo, setApplicantInfo] = useState({ name: localStorage.getItem("loggedInUserFullName") ?? "", email: localStorage.getItem("loggedInUserEmail") ?? "" });

  const [loggedInUser, setLoggedInUser] = useState({ name: localStorage.getItem("loggedInUserFullName") ?? "", email: localStorage.getItem("loggedInUserEmail") ?? "" });

  const { comment, commentList, commentsAccordionExpanded, handleCommentChange, handleAddComment, handleCommentsAccordionToggle } = useComments(bonusDetails, selectedRole);
  const { historyItems, historyAccordionExpanded, handleHistoryAccordionToggle } = useHistory(bonusDetails);


  // Transformation Logic (wrap in useMemo for performance)
  const { groupedProjectOptions, allRegionNames } = useMemo(() => {
    const grouped = Object.entries(
      projects.reduce((acc, project) => {
        const region = project.hierLevel3 || "Other";
        if (!acc[region]) acc[region] = [];
        acc[region].push({
          value: project.projectNumber.toString(),
          label: `${project.projectNumber} - ${project.projectName}`,
          region: project.hierLevel3,
          projectNumber: project.projectNumber,
          projectName: project.projectName
        });
        return acc;
      }, {} as Record<string, any[]>)
    )
      .sort(([regionA], [regionB]) => regionA.localeCompare(regionB))
      .map(([region, options]) => ({
        label: region,
        options: options.sort((a, b) => a.projectName.localeCompare(b.projectName))
      }));

    return {
      groupedProjectOptions: grouped,
      allRegionNames: grouped.map(g => g.label)
    };
  }, [projects]);

  const [formData, setFormData] = useState<FormData>({

    managerName: "",
    managerEmail: "",

    applicationType: "",
    bonusRecipientName: "",
    bonusRecipientEmail: "",
    submitterName: (localStorage.getItem("loggedInUserFullName") || "").replace(/"/g, "") || "",
    submitterEmail: (localStorage.getItem("loggedInUserEmail") || "").replace(/"/g, "") || "",
    vpName: "",
    vpEmail: "",
    opsName: FORM_CONSTANTS.OPS.NAME,
    opsEmail: FORM_CONSTANTS.OPS.EMAIL,

    hasOverriddenHierarchy: false,
    jobTitle: "",
    jobFamily: "",

    communityNumber: "", year: prevQuarterYear.year.toString(), quarter: prevQuarterYear.quarter.toString(),
    operationalEfficiency: {
      subdivisionProduction: { benchmark: "", actualProduction: "", benchmarkPercent: "", percentOfBenchmark: "", bonusPercentEarned: "", bonusValue: "0.30", score: "" },
      extraLaborAndMaterial: { benchmark: "70%", actualELM: "", goal: "", percentOfRevenue: "", bonusPercentEarned: "", bonusValue: "0.2", score: ".14" },
      customerCareExpense: { actualExpense: "", budget: "", percentOfBudget: "", bonusPercentEarned: "", bonusValue: "0.1", score: "" },
    },
    customerExperience: {
      customerCareCalls: { benchmark: "70%", under7Completed: "", totalCallsCompleted: "", percentCompletedUnder7: "", percentVsBenchmark: "", bonusPercentEarned: "", bonusValue: "0.2", score: "0.14" },
      postCloseTouchpoint: { benchmark: "100%", meetingsCompleted30Days: "", totalMeetings: "", percentCompleted: "", percentVsBenchmark: "", bonusPercentEarned: "", bonusValue: "0.1", score: "0.07" },
      surveys: {
        trustBuilderScore: { surveysReceived: "", sumOfScores: "", avgScore: "" },
        closingSurveyScore: { complete: "", onTime: "", customerRatingScore: "" },
        overallCombinedSurveyScore: { trustBuilders: "", complete: "", onTime: "", customerRatingScore: "", totalAvgScore: "" },
        bonusPercentEarned: "", bonusValue: "0.1", score: "",
      },
    },
    bonusCalculation: {
      totalAchievementScore: { revenueOfHomesClosed: "", overridePercentage: "", bonusPoolAvailableAt100: "", totalMaxAt120: "" },
      totalScore: "", percentage: "", bonusPool: "", quarterlyFixedBonusRate: "0", totalBonusPayout: "",
    },
    signatures: { divisionManagerSignature: "", divisionManagerDate: "", vpSignature: "", vpDate: "", employeeSignature: "", employeeDate: "" },
  });

  const initQueryData = useMemo(() => {
    if (!initQueryParam) return null;
    try {
      return JSON.parse(atob(decodeURIComponent(initQueryParam)));
    } catch (e) {
      console.error("Failed to parse ?init= param", e);
      return null;
    }
  }, [initQueryParam]);

  // ── All useEffects preserved exactly ─────────────────────────────────────

  // Initialize the collapse state once options are loaded
  useEffect(() => {
    if (allRegionNames.length > 0 && collapsedCommunityGroups.length === 0) {
      setCollapsedCommunityGroups(allRegionNames);
    }
  }, [allRegionNames]);

  useEffect(() => {
    if (!initQueryData) return;
    if (bonusDetails || hasValidId) return;
    try {
      setBonusSheetTitle(`${initQueryData.applicationType} Quarterly Bonus`);
      setFormData(prev => ({
        ...prev,
        managerName: initQueryData.bonusRecipientName || prev.managerName,
        managerEmail: initQueryData.bonusRecipientEmail || prev.managerEmail,

        hasOverriddenHierarchy: initQueryData?.hasOverriddenHierarchy || false,
        jobTitle: initQueryData?.jobTitle || prev.jobTitle,
        jobFamily: initQueryData?.jobFamily || prev.jobFamily,

        applicationType: initQueryData.applicationType || prev.applicationType,
        bonusRecipientName: initQueryData.bonusRecipientName || prev.bonusRecipientName,
        bonusRecipientEmail: initQueryData.bonusRecipientEmail || prev.bonusRecipientEmail,
        submitterName: initQueryData.submitterName || prev.submitterName,
        submitterEmail: initQueryData.submitterEmail || prev.submitterEmail,
        vpName: initQueryData.vpName || prev.vpName,
        vpEmail: initQueryData.vpEmail || prev.vpEmail,
        year: initQueryData.year?.toString() || prev.year, quarter: initQueryData.quarter?.toString() || prev.quarter, signatures: { ...prev.signatures, vpSignature: initQueryData.vpName || prev.signatures.vpSignature }
      }));
      setApplicantInfo({ name: initQueryData.applicantName || "", email: initQueryData.applicantEmail || "" });
    } catch (e) { console.error("Failed to parse ?init= param", e); }
  }, [initQueryData]);

  useEffect(() => {
    if (projects.length > 0) {
      setProjectOptions(projects.map((p: Project) => ({ value: p.projectNumber.toString(), label: `${p.projectNumber} - ${p.projectName}` })));
    }
  }, [projects]);

  useEffect(() => {
    if (!bonusDetails && !hasValidId && !initQueryData && templates && templates?.jsonData && templates?.hierarchy) {
      try {
        const td = JSON.parse(templates.jsonData);
        if (templates.hierarchy?.immediateSupervisorName && templates.hierarchy?.immediateSupervisorEmail)
          setApplicantInfo({ name: templates.hierarchy.immediateSupervisorName, email: templates.hierarchy.immediateSupervisorEmail });
        setFormData(prev => ({
          ...prev,
          managerName: ((templates.hierarchy?.employeeName || localStorage.getItem("loggedInUserFullName") || "").replace(/"/g, "") || "").toString(),
          managerEmail: ((templates.hierarchy?.employeeEmail || localStorage.getItem("loggedInUserEmail") || "").replace(/"/g, "") || "").toString(),
          applicationType: td.applicationType || prev.applicationType,
          bonusRecipientName: td.bonusRecipientName || prev.bonusRecipientName,
          bonusRecipientEmail: td.bonusRecipientEmail || prev.bonusRecipientEmail,
          submitterName: td.submitterName || prev.submitterName,
          submitterEmail: td.submitterEmail || prev.submitterEmail,
          vpName: td.vpName || prev.vpName,
          vpEmail: td.vpEmail || prev.vpEmail,

          jobTitle: prev.jobTitle || td.jobTitle || "",
          jobFamily: prev.jobFamily || td.jobFamily || "",
          hasOverriddenHierarchy: td?.hasOverriddenHierarchy || false,

          operationalEfficiency: { subdivisionProduction: td?.operationalEfficiency?.subdivisionProduction || prev.operationalEfficiency.subdivisionProduction, extraLaborAndMaterial: td?.operationalEfficiency?.extraLaborAndMaterial || prev.operationalEfficiency.extraLaborAndMaterial, customerCareExpense: td?.operationalEfficiency?.customerCareExpense || prev.operationalEfficiency.customerCareExpense },
          customerExperience: { customerCareCalls: td?.customerExperience?.customerCareCalls || prev.customerExperience.customerCareCalls, postCloseTouchpoint: td?.customerExperience?.postCloseTouchpoint || prev.customerExperience.postCloseTouchpoint, surveys: td?.customerExperience?.surveys || prev.customerExperience.surveys },
          bonusCalculation: { totalAchievementScore: td?.bonusCalculation?.totalAchievementScore || prev.bonusCalculation.totalAchievementScore, totalScore: td?.bonusCalculation?.totalScore || prev.bonusCalculation.totalScore, percentage: td?.bonusCalculation?.percentage || prev.bonusCalculation.percentage, bonusPool: td?.bonusCalculation?.bonusPool || prev.bonusCalculation.bonusPool, quarterlyFixedBonusRate: td?.bonusCalculation?.quarterlyFixedBonusRate || "0", totalBonusPayout: td?.bonusCalculation?.totalBonusPayout || prev.bonusCalculation.totalBonusPayout },
          signatures: td?.signatures || prev.signatures,
        }));
      } catch (error) { console.error("Error parsing template data:", error); }
    }
  }, [templates]);

  useEffect(() => {
    if (bonusDetails && hasValidId) {
      try {
        const rawBonusSheet = getBonusSheetJsonData(bonusDetails);
        if (!rawBonusSheet) return;
        const bsd = parseBonusSheetJsonData(rawBonusSheet) as any;
        setBonusSheetData(bsd);

        setBonusSheetTitle(`${bonusDetails.applicationType} Quarterly Bonus`);
        const projectNumbers = bsd?.communityNumber || "";
        if (projectNumbers) {
          const arr = projectNumbers.split(',').map((n: string) => n.trim());
          const opts = arr.map((num: string) => { const p = projects.find((pr: Project) => pr.projectNumber.toString() === num); return p ? { value: p.projectNumber.toString(), label: `${p.projectNumber} - ${p.projectName}` } : null; }).filter(Boolean) as ProjectOptionType[];
          setSelectedProjectOptions(opts);
        }

        setFormData(prev => {
          const recipientNameLoaded = bsd?.bonusRecipientName || bsd?.managerName || bsd?.applicantName || bonusDetails?.employeeName || prev.bonusRecipientName;
          const recipientEmailLoaded = bsd?.bonusRecipientEmail || bsd?.managerEmail || bsd?.applicantEmail || bonusDetails?.bonusRecipientEmail || prev.bonusRecipientEmail;
          return {
            ...prev,
            managerName: recipientNameLoaded,
            managerEmail: recipientEmailLoaded,

            applicationType: bonusDetails?.applicationType.toString() || prev.applicationType,
            bonusRecipientName: recipientNameLoaded,
            bonusRecipientEmail: recipientEmailLoaded,
            submitterName: bonusDetails?.submittedByName || prev.submitterName,
            submitterEmail: bonusDetails?.submittedBy || prev.submitterEmail,
            vpName: bsd?.vpName || prev.vpName,
            vpEmail: bsd?.vpEmail || prev.vpEmail,
            opsName: bsd?.opsName || prev.opsName,
            opsEmail: bsd?.opsEmail || prev.opsEmail,

            jobTitle: bsd?.jobTitle || prev.jobTitle,
            jobFamily: bsd?.jobFamily || prev.jobFamily,
            hasOverriddenHierarchy: bonusDetails?.hasOverriddenHierarchy || false,

            communityNumber: bsd?.communityNumber || prev.communityNumber, year: bsd?.year || prev.year, quarter: bsd?.quarter || prev.quarter, operationalEfficiency: bsd?.operationalEfficiency || prev.operationalEfficiency, customerExperience: bsd?.customerExperience || prev.customerExperience, bonusCalculation: { ...prev.bonusCalculation, ...bsd?.bonusCalculation, quarterlyFixedBonusRate: bsd?.bonusCalculation?.quarterlyFixedBonusRate || prev.bonusCalculation.quarterlyFixedBonusRate || "0" }, signatures: bsd?.signatures || prev.signatures
          };
        });

        if (bonusDetails.createdBy) setApplicantInfo(prev => ({ ...prev, email: bonusDetails.createdBy }));
      } catch (error) { console.error("Error parsing bonus sheet data:", error); }
    }
  }, [bonusDetails, id, projects]);

  const derivedCalculations = useMemo(() => {
    const sp = formData.operationalEfficiency.subdivisionProduction;
    const elm = formData.operationalEfficiency.extraLaborAndMaterial;
    const cce = formData.operationalEfficiency.customerCareExpense;
    const cc = formData.customerExperience.customerCareCalls;
    const pct = formData.customerExperience.postCloseTouchpoint;
    const sur = formData.customerExperience.surveys;

    const subB = parseFloat(sp.benchmark || "0"), subA = parseFloat(sp.actualProduction || "0");
    const subPct = subB ? subA / subB : 0;
    const subBPE = Math.max(0, Math.min(subPct, 1.2)) * (subPct >= 0.7 ? 100 : 0);
    const subBV = parseFloat(sp.bonusValue || "0.30");
    const subScore = subBPE * subBV;

    const elmB = parseFloat(elm.benchmark || "0"), elmA = parseFloat(elm.actualELM || "0");
    const elmGoal = elmB, elmPctRev = elmA && elmGoal ? elmA / elmGoal : 0;
    const elmInv = elmPctRev !== 0 ? 1 / elmPctRev : 0;
    const elmBPE = Math.max(0, Math.min(elmInv, 1.2)) * (elmInv >= 0.7 ? 100 : 0);
    const elmBV = parseFloat(elm.bonusValue || "0.2"), elmScore = elmBPE * elmBV;

    const expA = parseFloat(cce.actualExpense || "0"), expB = parseFloat(cce.budget || "0");
    const expPct = expB ? expA / expB : 0, expInv = expPct !== 0 ? 1 / expPct : 0;
    const expBPE = Math.max(0, Math.min(expInv, 1.2)) * (expInv >= 0.7 ? 1 : 0) * 100;
    const expBV = parseFloat(cce.bonusValue || "0.1"), expScore = expBPE * expBV;

    const ccU7 = parseFloat(cc.under7Completed || "0"), ccTot = parseFloat(cc.totalCallsCompleted || "0");
    const ccPctU7 = ccTot ? (ccU7 / ccTot) * 100 : 0;
    const ccPctVB = (ccPctU7 / 70) * 100;
    const ccBPE = Math.max(0, Math.min(ccPctVB, 120)) * (ccPctVB >= 70 ? 1 : 0);
    const ccBV = parseFloat(cc.bonusValue || "0.2"), ccScore = ccBPE * ccBV;

    const mC = parseFloat(pct.meetingsCompleted30Days || "0"), mT = parseFloat(pct.totalMeetings || "0");
    const mPct = mT ? (mC / mT) * 100 : 0, mPctVB = (mPct / 100) * 100;
    const mBPE = mPctVB < 70 ? 0 : Math.min(mPctVB, 100);
    const mBV = parseFloat(pct.bonusValue || "0.1"), mScore = mBPE * mBV;

    const tbR = parseFloat(sur.trustBuilderScore.surveysReceived || "0");
    const tbA = parseFloat(sur.trustBuilderScore.avgScore || "0");
    const sCC = parseFloat(sur.closingSurveyScore.complete || "0");
    const sOT = parseFloat(sur.closingSurveyScore.onTime || "0");
    const sCR = parseFloat(sur.closingSurveyScore.customerRatingScore || "0");
    const avgTotal = tbR > 0 ? (tbA + sCC + sOT + sCR) / 4 : 0;
    let surBPE = 0;
    if (avgTotal >= 5.0) surBPE = 120; else if (avgTotal >= 4.8) surBPE = 110; else if (avgTotal >= 4.6) surBPE = 100;
    else if (avgTotal >= 4.4) surBPE = 90; else if (avgTotal >= 4.2) surBPE = 80; else if (avgTotal >= 4.0) surBPE = 70;
    const surBV = parseFloat(sur.bonusValue || "0.1"), surScore = surBPE * surBV;

    const totalScore = Math.ceil(subScore + elmScore + expScore + ccScore + mScore + surScore);
    const pctage = totalScore / 100;
    const qfbr = parseFloat(formData.bonusCalculation.quarterlyFixedBonusRate || "0");
    const totalPayout = qfbr * pctage;

    return {
      subdivisionPercentOfBenchmark: subPct.toFixed(2), subdivisionBonusPercentEarned: subBPE.toFixed(2), subdivisionScore: subScore.toFixed(2),
      elmGoal: elmGoal.toFixed(2), elmPercentOfRevenue: elmPctRev.toFixed(2), elmBonusPercentEarned: elmBPE.toFixed(2), elmScore: elmScore.toFixed(2),
      expensePercentOfBudget: (expPct * 100).toFixed(2), expenseBonusPercentEarned: expBPE.toFixed(2), expenseScore: expScore.toFixed(2),
      callsPercentUnder7: ccPctU7.toFixed(2), callsPercentVsBenchmark: ccPctVB.toFixed(2), callsBonusPercentEarned: ccBPE.toFixed(2), callsScore: ccScore.toFixed(2),
      meetingsPercentCompleted: mPct.toFixed(2), meetingsPercentVsBenchmark: mPctVB.toFixed(2), meetingsBonusPercentEarned: mBPE.toFixed(2), meetingsScore: mScore.toFixed(2),
      overallTrustBuilders: tbA.toFixed(2), overallComplete: sCC.toFixed(2), overallOnTime: sOT.toFixed(2), overallCustomerRating: sCR.toFixed(2),
      overallTotalAvgScore: avgTotal.toFixed(2), surveysBonusPercentEarned: surBPE.toFixed(2), surveysScore: surScore.toFixed(2),
      totalScore: totalScore.toFixed(2), percentage: pctage.toFixed(2), totalBonusPayout: totalPayout.toFixed(2),
    };
  }, [
    formData.operationalEfficiency.subdivisionProduction.benchmark, formData.operationalEfficiency.subdivisionProduction.actualProduction, formData.operationalEfficiency.subdivisionProduction.bonusValue,
    formData.operationalEfficiency.extraLaborAndMaterial.benchmark, formData.operationalEfficiency.extraLaborAndMaterial.actualELM, formData.operationalEfficiency.extraLaborAndMaterial.bonusValue,
    formData.operationalEfficiency.customerCareExpense.actualExpense, formData.operationalEfficiency.customerCareExpense.budget, formData.operationalEfficiency.customerCareExpense.bonusValue,
    formData.customerExperience.customerCareCalls.under7Completed, formData.customerExperience.customerCareCalls.totalCallsCompleted, formData.customerExperience.customerCareCalls.bonusValue,
    formData.customerExperience.postCloseTouchpoint.meetingsCompleted30Days, formData.customerExperience.postCloseTouchpoint.totalMeetings, formData.customerExperience.postCloseTouchpoint.bonusValue,
    formData.customerExperience.surveys.trustBuilderScore.surveysReceived, formData.customerExperience.surveys.trustBuilderScore.avgScore,
    formData.customerExperience.surveys.closingSurveyScore.complete, formData.customerExperience.surveys.closingSurveyScore.onTime, formData.customerExperience.surveys.closingSurveyScore.customerRatingScore,
    formData.customerExperience.surveys.bonusValue, formData.bonusCalculation.quarterlyFixedBonusRate,
  ]);

  useEffect(() => {
    setFormData(prev => {
      if (
        prev.operationalEfficiency.subdivisionProduction.percentOfBenchmark === derivedCalculations.subdivisionPercentOfBenchmark &&
        prev.operationalEfficiency.subdivisionProduction.bonusPercentEarned === derivedCalculations.subdivisionBonusPercentEarned &&
        prev.operationalEfficiency.subdivisionProduction.score === derivedCalculations.subdivisionScore &&
        prev.operationalEfficiency.extraLaborAndMaterial.goal === derivedCalculations.elmGoal &&
        prev.operationalEfficiency.extraLaborAndMaterial.percentOfRevenue === derivedCalculations.elmPercentOfRevenue &&
        prev.operationalEfficiency.extraLaborAndMaterial.bonusPercentEarned === derivedCalculations.elmBonusPercentEarned &&
        prev.operationalEfficiency.extraLaborAndMaterial.score === derivedCalculations.elmScore &&
        prev.operationalEfficiency.customerCareExpense.percentOfBudget === derivedCalculations.expensePercentOfBudget &&
        prev.operationalEfficiency.customerCareExpense.bonusPercentEarned === derivedCalculations.expenseBonusPercentEarned &&
        prev.operationalEfficiency.customerCareExpense.score === derivedCalculations.expenseScore &&
        prev.customerExperience.customerCareCalls.percentCompletedUnder7 === derivedCalculations.callsPercentUnder7 &&
        prev.customerExperience.customerCareCalls.percentVsBenchmark === derivedCalculations.callsPercentVsBenchmark &&
        prev.customerExperience.customerCareCalls.bonusPercentEarned === derivedCalculations.callsBonusPercentEarned &&
        prev.customerExperience.customerCareCalls.score === derivedCalculations.callsScore &&
        prev.customerExperience.postCloseTouchpoint.percentCompleted === derivedCalculations.meetingsPercentCompleted &&
        prev.customerExperience.postCloseTouchpoint.percentVsBenchmark === derivedCalculations.meetingsPercentVsBenchmark &&
        prev.customerExperience.postCloseTouchpoint.bonusPercentEarned === derivedCalculations.meetingsBonusPercentEarned &&
        prev.customerExperience.postCloseTouchpoint.score === derivedCalculations.meetingsScore &&
        prev.customerExperience.surveys.overallCombinedSurveyScore.trustBuilders === derivedCalculations.overallTrustBuilders &&
        prev.customerExperience.surveys.overallCombinedSurveyScore.complete === derivedCalculations.overallComplete &&
        prev.customerExperience.surveys.overallCombinedSurveyScore.onTime === derivedCalculations.overallOnTime &&
        prev.customerExperience.surveys.overallCombinedSurveyScore.customerRatingScore === derivedCalculations.overallCustomerRating &&
        prev.customerExperience.surveys.overallCombinedSurveyScore.totalAvgScore === derivedCalculations.overallTotalAvgScore &&
        prev.customerExperience.surveys.bonusPercentEarned === derivedCalculations.surveysBonusPercentEarned &&
        prev.customerExperience.surveys.score === derivedCalculations.surveysScore &&
        prev.bonusCalculation.totalScore === derivedCalculations.totalScore &&
        prev.bonusCalculation.percentage === derivedCalculations.percentage &&
        prev.bonusCalculation.totalBonusPayout === derivedCalculations.totalBonusPayout
      ) return prev;
      return {
        ...prev,
        operationalEfficiency: {
          ...prev.operationalEfficiency,
          subdivisionProduction: { ...prev.operationalEfficiency.subdivisionProduction, percentOfBenchmark: derivedCalculations.subdivisionPercentOfBenchmark, bonusPercentEarned: derivedCalculations.subdivisionBonusPercentEarned, score: derivedCalculations.subdivisionScore },
          extraLaborAndMaterial: { ...prev.operationalEfficiency.extraLaborAndMaterial, goal: derivedCalculations.elmGoal, percentOfRevenue: derivedCalculations.elmPercentOfRevenue, bonusPercentEarned: derivedCalculations.elmBonusPercentEarned, score: derivedCalculations.elmScore },
          customerCareExpense: { ...prev.operationalEfficiency.customerCareExpense, percentOfBudget: derivedCalculations.expensePercentOfBudget, bonusPercentEarned: derivedCalculations.expenseBonusPercentEarned, score: derivedCalculations.expenseScore },
        },
        customerExperience: {
          ...prev.customerExperience,
          customerCareCalls: { ...prev.customerExperience.customerCareCalls, percentCompletedUnder7: derivedCalculations.callsPercentUnder7, percentVsBenchmark: derivedCalculations.callsPercentVsBenchmark, bonusPercentEarned: derivedCalculations.callsBonusPercentEarned, score: derivedCalculations.callsScore },
          postCloseTouchpoint: { ...prev.customerExperience.postCloseTouchpoint, percentCompleted: derivedCalculations.meetingsPercentCompleted, percentVsBenchmark: derivedCalculations.meetingsPercentVsBenchmark, bonusPercentEarned: derivedCalculations.meetingsBonusPercentEarned, score: derivedCalculations.meetingsScore },
          surveys: { ...prev.customerExperience.surveys, overallCombinedSurveyScore: { ...prev.customerExperience.surveys.overallCombinedSurveyScore, trustBuilders: derivedCalculations.overallTrustBuilders, complete: derivedCalculations.overallComplete, onTime: derivedCalculations.overallOnTime, customerRatingScore: derivedCalculations.overallCustomerRating, totalAvgScore: derivedCalculations.overallTotalAvgScore }, bonusPercentEarned: derivedCalculations.surveysBonusPercentEarned, score: derivedCalculations.surveysScore },
        },
        bonusCalculation: { ...prev.bonusCalculation, totalScore: derivedCalculations.totalScore, percentage: derivedCalculations.percentage, totalBonusPayout: derivedCalculations.totalBonusPayout },
      };
    });
  }, [derivedCalculations]);

  useEffect(() => {
    return () => {
      if (under7DebounceRef.current) clearTimeout(under7DebounceRef.current);
      if (meetingsDebounceRef.current) clearTimeout(meetingsDebounceRef.current);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, options?: { type?: 'number' | 'decimal' | 'text'; maxLength?: number; minLength?: number; max?: number; min?: number }) => {
    let { name, value } = e.target;
    if (options) { if (options.maxLength !== undefined && value.length > options.maxLength) return; if (options.minLength !== undefined && value !== '' && value.length < options.minLength) return; }
    value = typeof value === "string" ? value.replace(/,/g, "").trim() : "";
    const type = options?.type || "text";
    if (type === "number") { if (!(value === '' || /^[0-9]*$/.test(value))) return; }
    else if (type === "decimal") { if (!(value === '' || /^\d*\.?\d{0,2}$/.test(value))) return; }
    if (name === "customerExperience.customerCareCalls.under7Completed") { const tc = parseInt(formData.customerExperience.customerCareCalls.totalCallsCompleted || "0"); const u7 = parseInt(value || "0"); if (formData.customerExperience.customerCareCalls.totalCallsCompleted && tc > 0 && u7 > tc) value = tc.toString(); }
    if (name === "customerExperience.customerCareCalls.totalCallsCompleted") {
      if (under7DebounceRef.current) clearTimeout(under7DebounceRef.current);
      setFormData(prev => setByPath(prev, name.split("."), value));
      const u7 = parseInt(formData.customerExperience.customerCareCalls.under7Completed || "0");
      const tcv = parseInt(value || "0");
      if (formData.customerExperience.customerCareCalls.under7Completed && u7 > 0 && value !== '' && tcv < u7)
        under7DebounceRef.current = setTimeout(() => setFormData(prev => setByPath(prev, ["customerExperience", "customerCareCalls", "under7Completed"], value)), 500);
      return;
    }
    if (name === "customerExperience.postCloseTouchpoint.meetingsCompleted30Days") { const tm = parseInt(formData.customerExperience.postCloseTouchpoint.totalMeetings || "0"); const mv = parseInt(value || "0"); if (formData.customerExperience.postCloseTouchpoint.totalMeetings && tm > 0 && mv > tm) value = tm.toString(); }
    if (name === "customerExperience.postCloseTouchpoint.totalMeetings") {
      if (meetingsDebounceRef.current) clearTimeout(meetingsDebounceRef.current);
      setFormData(prev => setByPath(prev, name.split("."), value));
      const m = parseInt(formData.customerExperience.postCloseTouchpoint.meetingsCompleted30Days || "0");
      const tmv = parseInt(value || "0");
      if (formData.customerExperience.postCloseTouchpoint.meetingsCompleted30Days && m > 0 && value !== '' && tmv < m)
        meetingsDebounceRef.current = setTimeout(() => setFormData(prev => setByPath(prev, ["customerExperience", "postCloseTouchpoint", "meetingsCompleted30Days"], value)), 500);
      return;
    }
    setFormData(prev => setByPath(prev, name.split("."), value));
  };

  const handleProjectSelect = (selected: readonly ProjectOptionType[]) => {
    const validSelected = (selected as ProjectOptionType[]).filter(o => o?.value !== undefined);
    setSelectedProjectOptions(validSelected);
    setFormData(prev => ({ ...prev, communityNumber: validSelected.map(o => o.value).join(', ') }));
  };

  const validateForm = () => {
    const errors: string[] = [];
    if (isReviewMode && (bonusDetails?.currentStatus === undefined || bonusDetails?.currentStatus === "Draft" || bonusDetails?.currentStatus === "Rejected")) {
      if (!formData.communityNumber) errors.push("Communities are not selected.");
      if (!formData.operationalEfficiency.subdivisionProduction.benchmark || formData.operationalEfficiency.subdivisionProduction.benchmark === "0") errors.push("Subdivision Production: Benchmark is required.");
      if (!formData.operationalEfficiency.subdivisionProduction.actualProduction) errors.push("Subdivision Production: Actual Production is required.");
      if (!formData.operationalEfficiency.extraLaborAndMaterial.benchmark || formData.operationalEfficiency.extraLaborAndMaterial.benchmark === "0") errors.push("Extra Labor and Material: Benchmark is required.");
      if (!formData.operationalEfficiency.extraLaborAndMaterial.actualELM) errors.push("Extra Labor and Material: Actual ELM is required.");
      if (!formData.operationalEfficiency.customerCareExpense.actualExpense) errors.push("Customer Care Expense: Actual Expense is required.");
      if (!formData.operationalEfficiency.customerCareExpense.budget || formData.operationalEfficiency.customerCareExpense.budget === "0") errors.push("Customer Care Expense: Budget is required.");
      if (!formData.customerExperience.customerCareCalls.under7Completed) errors.push("Customer Care Calls: Under 7 Completed is required.");
      if (!formData.customerExperience.customerCareCalls.totalCallsCompleted) errors.push("Customer Care Calls: Total Calls Completed is required.");
      if (!formData.customerExperience.postCloseTouchpoint.meetingsCompleted30Days) errors.push("Post Close Touchpoint: Meetings Completed 30 Days is required.");
      if (!formData.customerExperience.postCloseTouchpoint.totalMeetings) errors.push("Post Close Touchpoint: Total Meetings is required.");
      if (!formData.customerExperience.surveys.trustBuilderScore.surveysReceived || formData.customerExperience.surveys.trustBuilderScore.surveysReceived === "0") errors.push("Surveys: Trust Builder Score - Surveys Received is required.");
      if (!formData.customerExperience.surveys.trustBuilderScore.avgScore) errors.push("Surveys: Trust Builder Score - Average Score is required.");
      if (!formData.customerExperience.surveys.closingSurveyScore.complete) errors.push("Surveys: Closing Survey Score - Complete is required.");
      if (!formData.customerExperience.surveys.closingSurveyScore.onTime) errors.push("Surveys: Closing Survey Score - On Time is required.");
      if (!formData.customerExperience.surveys.closingSurveyScore.customerRatingScore) errors.push("Surveys: Closing Survey Score - Customer Rating Score is required.");
      if (!formData.bonusCalculation.quarterlyFixedBonusRate || formData.bonusCalculation.quarterlyFixedBonusRate === "0") errors.push("Bonus Calculations: Quarterly Fixed Bonus Rate is required.");
    }
    return errors;
  };

  const hasOverriddenHierarchy = initQueryData?.hasOverriddenHierarchy || bonusDetails?.hasOverriddenHierarchy;
  const recipientEmail = initQueryData?.bonusRecipientDetails?.Email || bonusDetails?.bonusRecipientEmail;
  const recipientName = initQueryData?.bonusRecipientDetails?.EmployeeName || bonusDetails?.employeeName;
  const recipientJobTitle = initQueryData?.bonusRecipientDetails?.JobTitle || bonusSheetData?.jobTitle || formData.jobTitle || "";
  const recipientJobFamily = initQueryData?.bonusRecipientDetails?.JobFamily || bonusSheetData?.jobFamily || formData.jobFamily || "";
  const applicationType = initQueryData?.applicationType || bonusDetails?.applicationType;

  const buildMetadata = () => {
    const recipientNameFinal = recipientName || formData.bonusRecipientName || bonusDetails?.employeeName || "";
    const recipientEmailFinal = recipientEmail || formData.bonusRecipientEmail || "";
    return {
      applicationType: applicationType || formData.applicationType || "",
      bonusRecipientName: recipientNameFinal,
      bonusRecipientEmail: recipientEmailFinal,
      managerName: recipientNameFinal,
      managerEmail: recipientEmailFinal,
      applicantName: recipientNameFinal,
      applicantEmail: recipientEmailFinal,
      submitterName: initQueryData?.submitterName || bonusDetails?.submittedByName || formData.submitterName || "",
      submitterEmail: initQueryData?.submitterEmail || bonusDetails?.submittedBy || formData.submitterEmail || "",
      vpName: formData.vpName || "",
      vpEmail: formData.vpEmail || "",
      opsName: formData.opsName || "",
      opsEmail: formData.opsEmail || "",
      hasOverriddenHierarchy: formData.hasOverriddenHierarchy || false,
      jobTitle: recipientJobTitle || formData?.jobTitle || "",
      jobFamily: recipientJobFamily || formData?.jobFamily || "",
    };
  };

  const getBonusSheetJson = () => btoa(JSON.stringify({
    communityNumber: formData.communityNumber,
    year: formData.year,
    quarter: formData.quarter,
    operationalEfficiency: formData.operationalEfficiency,
    customerExperience: formData.customerExperience,
    bonusCalculation: { ...formData.bonusCalculation, quarterlyFixedBonusRate: formData.bonusCalculation.quarterlyFixedBonusRate },
    signatures: formData.signatures,
    ...buildMetadata(),
  }));

  const breadcrumbPages = [{ name: FORM_CONSTANTS.BREADCRUMB.TITLE, href: FORM_CONSTANTS.BREADCRUMB.PATH, current: true, icon: (props: React.ComponentProps<"svg">) => <ClipboardDocumentCheckIcon {...props} className="w-6 h-6 text-primary-500 transition duration-75 group-hover:text-gray-900" /> }];

  if (isLoading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg-page)" }}>
      <div style={{ color: "var(--text-muted)" }}>Loading…</div>
    </div>
  );

  const status = bonusDetails?.currentStatus?.replaceAll("_", " ") ?? "Draft";
  const statusColors: Record<string, React.CSSProperties> = {
    Draft: { background: "#FEF9C3", color: "#854D0E", border: "1px solid #FDE047" },
    Submitted: { background: "#DBEAFE", color: "#1E40AF", border: "1px solid #93C5FD" },
    Approved: { background: "#DCFCE7", color: "#166534", border: "1px solid #86EFAC" },
    Rejected: { background: "#FEE2E2", color: "#991B1B", border: "1px solid #FCA5A5" },
    Paid: { background: "#D1FAE5", color: "#065F46", border: "1px solid #6EE7B7" },
  };
  const statusStyle = statusColors[status] ?? statusColors["Draft"];

  //#region Community Number Options with Grouping and Sorting

  const filterCommunityOption = (candidate: any, input: string) => {
    const search = input.toLowerCase();
    const data = candidate.data;
    return (
      data.projectName.toLowerCase().includes(search) ||
      data.projectNumber.toLowerCase().includes(search) ||
      data.region.toLowerCase().includes(search)
    );
  };

  const toggleCommunityGroup = (groupLabel: string) => {
    setCollapsedCommunityGroups(prev =>
      prev.includes(groupLabel) ? prev.filter(l => l !== groupLabel) : [...prev, groupLabel]
    );
  };

  //#endregion Community Number Options with Grouping and Sorting


  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--bg-page)" }}>
      <div className="flex flex-col">
        <main className="flex flex-col pb-8">
          <div className={`mx-auto w-full pt-6 ${FORM_CONSTANTS.LAYOUT.MAX_WIDTH} ${FORM_CONSTANTS.LAYOUT.PADDING}`}>
            <Breadcrumb pages={breadcrumbPages} className="mb-6 ml-2" />

            <div style={{ background: "var(--bg-surface)", borderRadius: 12, border: "1px solid var(--border-default)", overflow: "hidden", boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>

              {/* ── Card header ─────────────────────────────────────────── */}
              <div style={{ borderBottom: "1px solid var(--border-section)", padding: "20px 24px", background: "var(--bg-form-header)" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 4 }}>
                  <h1 style={{ margin: 0, fontSize: "1.125rem", fontWeight: 700, color: "var(--text-primary)", letterSpacing: "-0.01em" }}>{bonusSheetTitle}</h1>
                  <span style={{ fontSize: 12, fontWeight: 600, padding: "3px 10px", borderRadius: 20, ...statusStyle }}>{status}</span>
                </div>
                <p style={{ margin: "0 0 16px", fontSize: 12, color: "var(--text-muted)", fontStyle: "italic", lineHeight: 1.5 }}>
                  Achievement of Targets is a subjective determination in the Employer's sole discretion. The Employer may change the percentages or Targets at any time.
                </p>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
                  {/* Manager Name */}
                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--text-muted)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.06em" }}>Bonus Recipient</label>
                    <input type="text" name="managerName" value={formData.managerName} readOnly
                      style={{ width: "100%", padding: "7px 10px", fontSize: 13, fontWeight: 500, color: "var(--text-primary)", background: "var(--bg-input-readonly)", border: "1px solid var(--border-readonly)", borderRadius: 7, outline: "none", cursor: "not-allowed", boxSizing: "border-box" }} />
                  </div>

                  {/* Community Number(s) — react-select, themed */}
                  <div style={{ gridColumn: "span 2" }}>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--text-muted)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.06em" }}>Community Number(s)</label>
                    <Select
                      isMulti
                      value={selectedProjectOptions}
                      onChange={handleProjectSelect}
                      options={groupedProjectOptions}
                      isDisabled={Boolean(bonusDetails?.bonusApplicationId)}
                      styles={getSelectStyles()}
                      placeholder="Select communities…"
                      filterOption={filterCommunityOption}
                      components={{
                        GroupHeading: (p) => <CustomCommunityGroupHeading {...p} toggleGroup={toggleCommunityGroup} collapsedGroups={collapsedCommunityGroups} />,
                        Group: (p) => <CustomCommunityGroup {...p} collapsedGroups={collapsedCommunityGroups} />
                      }}
                      onInputChange={(inputValue) => {
                        // Auto-expand when typing, collapse all when cleared
                        if (inputValue.length > 0) {
                          setCollapsedCommunityGroups([]);
                        } else {
                          setCollapsedCommunityGroups(allRegionNames);
                        }
                      }}
                    />
                  </div>

                  {/* Year */}
                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--text-muted)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.06em" }}>Year</label>
                    <input type="text" name="year" value={formData.year} readOnly
                      style={{ width: "100%", padding: "7px 10px", fontSize: 13, color: "var(--text-readonly)", background: "var(--bg-input-readonly)", border: "1px solid var(--border-readonly)", borderRadius: 7, outline: "none", cursor: "not-allowed", boxSizing: "border-box" }} />
                  </div>

                  {/* Quarter */}
                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--text-muted)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.06em" }}>Quarter</label>
                    <input type="text" name="quarter" value={formData.quarter} readOnly
                      style={{ width: "100%", padding: "7px 10px", fontSize: 13, color: "var(--text-readonly)", background: "var(--bg-input-readonly)", border: "1px solid var(--border-readonly)", borderRadius: 7, outline: "none", cursor: "not-allowed", boxSizing: "border-box" }} />
                  </div>
                </div>
              </div>

              {/* Override active banner */}
              {hasOverriddenHierarchy && (
                <div style={{ background: "#fff5f5", border: "1.5px solid #f87171", borderRadius: "8px", padding: "12px 16px", fontSize: 12, color: "#991b1b", display: "flex", flexDirection: "column", gap: 6 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 14 }}>⚠️</span>
                    <span style={{ fontWeight: 700, fontSize: 13 }}>Override Active: Employee Hierarchy is Overridden</span>
                  </div>
                  <span style={{ color: "#7f1d1d", lineHeight: 1.5 }}>
                    {status === "Draft"
                      ? "This recipient was selected with an active hierarchy override — all Construction & Customer Care employees were available for selection, not just direct reports."
                      : "This recipient was selected under a hierarchy override — the standard reporting hierarchy was not enforced at the time of selection."}
                  </span>
                  {(recipientName || recipientJobTitle || recipientJobFamily || applicationType) && (
                    <div style={{ marginTop: 4, padding: "8px 10px", background: "#fee2e2", borderRadius: "6px", display: "flex", flexWrap: "wrap", gap: "4px 20px", color: "#7f1d1d" }}>
                      {recipientName && <span><span style={{ fontWeight: 600 }}>Recipient:</span> {recipientName}</span>}
                      {recipientJobTitle && <span><span style={{ fontWeight: 600 }}>Recipient Job Title:</span> {recipientJobTitle}</span>}
                      {recipientJobFamily && <span><span style={{ fontWeight: 600 }}>Job Family:</span> {recipientJobFamily}</span>}
                      {applicationType && <span><span style={{ fontWeight: 600 }}>Application Type:</span> {applicationType}</span>}
                    </div>
                  )}
                </div>
              )}

              {/* ── OPERATIONAL EFFICIENCY ─────────────────────────────── */}
              <div style={{ borderTop: "1px solid var(--border-section)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 24px", background: "var(--bg-section-header)", borderBottom: "1px solid var(--border-section)" }}>
                  <div style={{ width: 3, height: 18, borderRadius: 2, background: "var(--brand-primary)", flexShrink: 0 }} />
                  <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", color: "var(--text-secondary)" }}>Operational Efficiency</span>
                </div>
                <SubdivisionProductionSubForm data={formData.operationalEfficiency.subdivisionProduction} isEditable={isEditable} percentOfTotalAchievementScore={30} onInputChange={handleInputChange} />
                <ExtraLaborAndMaterialSubForm data={formData.operationalEfficiency.extraLaborAndMaterial} isEditable={isEditable} percentOfTotalAchievementScore={20} onInputChange={handleInputChange} />
                <CustomerCareExpenseSubForm data={formData.operationalEfficiency.customerCareExpense} isEditable={isEditable} onInputChange={handleInputChange} />
              </div>

              {/* ── CUSTOMER EXPERIENCE ────────────────────────────────── */}
              <div style={{ borderTop: "1px solid var(--border-section)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 24px", background: "var(--bg-section-header)", borderBottom: "1px solid var(--border-section)" }}>
                  <div style={{ width: 3, height: 18, borderRadius: 2, background: "var(--brand-primary)", flexShrink: 0 }} />
                  <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", color: "var(--text-secondary)" }}>Customer Experience</span>
                </div>
                <CustomerCareCallsSubForm data={formData.customerExperience.customerCareCalls} isEditable={isEditable} percentOfTotalAchievementScore={20} onInputChange={handleInputChange} />
                <PostCloseTouchpointSubForm data={formData.customerExperience.postCloseTouchpoint} isEditable={isEditable} onInputChange={handleInputChange} />
                <SurveysSubForm data={formData.customerExperience.surveys} isEditable={isEditable} onInputChange={handleInputChange} />
              </div>

              {/* ── BONUS CALCULATION ──────────────────────────────────── */}
              <div style={{ borderTop: "1px solid var(--border-section)" }}>
                <BonusCalculationsSPMSubForm data={formData.bonusCalculation} isEditable={isEditable} onInputChange={handleInputChange} />
              </div>
            </div>
          </div>
        </main>
      </div>

      <BonusApplicationHistory historyItems={historyItems} comments={commentList} historyAccordionExpanded={historyAccordionExpanded} onAccordionToggle={handleHistoryAccordionToggle} />
      <CommentComponent comment={comment} commentList={commentList} commentsAccordionExpanded={commentsAccordionExpanded} onCommentChange={handleCommentChange} onAddComment={handleAddComment} onAccordionToggle={handleCommentsAccordionToggle} />

      <div style={{ padding: "14px 24px", borderTop: "1px solid var(--border-section)", background: "var(--bg-surface-raised)", display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <ActionButtons
          isReviewMode={isReviewMode}
          bonusApplicationId={id && parseInt(id) ? parseInt(id) : bonusDetails?.bonusApplicationId}
          currentStatus={bonusDetails?.currentStatus ?? null}
          bonusSheetJson={getBonusSheetJson()}
          projectNumbers={formData.communityNumber}
          amount={parseFloat(formData.bonusCalculation.totalBonusPayout) || 0}
          bonusRecipientEmail={formData.bonusRecipientEmail}
          submitterEmail={formData.submitterEmail}
          submitterName={formData.submitterName}
          vpEmail={formData.vpEmail}
          applicationType={formData.applicationType}
          hasOverriddenHierarchy={initQueryData?.hasOverriddenHierarchy || bonusDetails?.hasOverriddenHierarchy || false}
          comments={commentList.filter((c: BonusApplicationComment) => c.commentId <= 0).map((c: BonusApplicationComment) => c.commentText)}
          formValidationErrors={validateForm()}
          recipientName={formData.managerName}
          recipientJobFamily={recipientJobFamily}
          recipientJobTitle={recipientJobTitle}
          loggedInUserName={loggedInUser.name}

        />
      </div>
    </div>
  );
};

export default SPMForm;